import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandMark } from "@/components/BrandMark";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Customer login — email OTP.
 *
 * Same two-step OTP flow as before (send-otp → verify-otp → JWT →
 * /customer/my-winnings). Visual layer rebuilt to match the marketing
 * / Explore look: cream paper background, black ink text, gold accent,
 * Anton headline, offset drop-shadow card. No functional changes.
 */
export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // ── Post-login destination ─────────────────────────────────────
  // Callers (e.g. the OfferPage login-gate) pass `?next=/offers/xyz`
  // so the customer lands exactly back where they started after
  // signing in. Fallback is My Winnings, the "expected" post-login
  // page. Any query params on the `next` value (e.g. `autoclaim=1`)
  // are preserved so downstream pages can react to the return.
  const nextParam = searchParams.get("next");
  const isSafeRelativeUrl = (u) => typeof u === "string" && u.startsWith("/") && !u.startsWith("//");
  const destination = isSafeRelativeUrl(nextParam) ? nextParam : "/customer/my-winnings";

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      await api.customerSendOtp({ email: email.trim(), name: name.trim() || undefined });
      toast.success("Code sent — check your inbox");
      setStep("code");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d?.message : d || "Couldn't send code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const r = await api.customerVerifyOtp({ email: email.trim(), code: code.trim() });
      localStorage.setItem("citydooh_customer_token", r.data.token);
      localStorage.setItem("citydooh_customer", JSON.stringify(r.data.customer || {}));
      toast.success("Signed in");
      navigate(destination);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "object" ? d?.message : d || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#0A0A0A] flex flex-col">
      {/* Slim brand bar — matches marketing header cadence without
          being a full nav (this is a focus page, not a browsing page). */}
      <header className="border-b-2 border-black bg-[#F7F4EC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0A0A] rounded-md" aria-label="City DOOH home" data-testid="login-brand">
            <BrandMark size="auth" />
          </Link>
          <Link
            to="/explore"
            className="text-xs sm:text-sm font-bold uppercase tracking-widest text-black hover:text-[#F5A800] inline-flex items-center gap-1"
            data-testid="login-back-to-explore"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Explore
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Offset drop-shadow card — same treatment used on marketing
              pricing tiles and campaign wizard steps. */}
          <div
            className="bg-white border-2 border-black rounded-2xl p-8"
            style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
            data-testid="customer-login-card"
          >
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-[#F5A800] font-black">
                Customer sign in
              </p>
              <h1
                className="text-4xl md:text-5xl leading-none mt-2"
                style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
              >
                YOUR WINS,<br />
                ONE <span className="text-[#F5A800]">TAP</span> AWAY.
              </h1>
              <p className="mt-3 text-sm text-black/70">
                Sign in with your email to keep track of every coupon you've won across CityDooh — no password to remember.
              </p>
            </div>

            {step === "email" ? (
              <form onSubmit={sendCode} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-black/80">Email</label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-12 border-2 border-black text-base focus-visible:ring-2 focus-visible:ring-[#F5A800]"
                    data-testid="customer-login-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-black/80">
                    Your name <span className="text-black/40 font-normal normal-case">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Riya"
                    className="h-12 border-2 border-black text-base focus-visible:ring-2 focus-visible:ring-[#F5A800]"
                    data-testid="customer-login-name"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 text-base font-black uppercase tracking-widest bg-[#0A0A0A] text-[#F5A800] border-2 border-black hover:bg-[#F5A800] hover:text-black transition-colors"
                  style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                  data-testid="customer-login-send-code"
                >
                  {busy ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>Send Code <ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-4" data-testid="customer-login-otp-step">
                <p className="text-sm text-black/70">
                  We sent a 6-digit code to <strong className="text-black">{email}</strong>. Expires in 10 minutes.
                </p>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-black/80">Verification code</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="123456"
                    className="h-14 border-2 border-black text-2xl font-mono tracking-[10px] text-center focus-visible:ring-2 focus-visible:ring-[#F5A800]"
                    data-testid="customer-login-code"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 text-base font-black uppercase tracking-widest bg-[#F5A800] text-black border-2 border-black hover:bg-[#0A0A0A] hover:text-[#F5A800] transition-colors"
                  style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                  data-testid="customer-login-verify"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & Sign In"}
                </Button>
                <button
                  type="button"
                  onClick={() => setStep("email")}
                  className="text-xs text-black/60 hover:text-black w-full uppercase tracking-widest font-bold"
                >
                  ← Use a different email
                </button>
              </form>
            )}
          </div>
          <p className="text-center text-xs text-black/50 mt-4">
            Are you a business?{" "}
            <Link to="/vendor/login" className="underline hover:text-black">Vendor Login</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
